import { SBRM, SBRMForm } from '@supplement-bacon/sbrm-react'
import { FormInstance } from 'antd'
import { forwardRef } from 'react'
import { langList } from '../../../factories/Utils'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  createEvent,
  deleteEvent,
  updateEvent,
} from '../../../store/membership/EventReducer'
import Create from './Create'
import Delete from './Delete'
import Update from './Update'

/**
 * `SBAPI.Post` wraps the Axios error as `{ message, error }`, and
 * `SBAPI.CreateDispatched` / `UpdateDispatched` wrap that again: Laravel's
 * validation payload sits two `error` levels down.
 */
interface SaveRejection {
  message?: string
  error?: {
    error?: {
      response?: {
        data?: { message?: string; errors?: Record<string, string[]> }
      }
    }
  }
}

// Maps a 422 on `<locale>.title` onto the matching localized field, then
// rethrows: SBRM only keeps the drawer open on a rejected handler
// (`sbrm-react` `handleCreate().then(close).catch(fireError)`).
const mapTitleErrorsAndRethrow = (
  rejection: SaveRejection,
  form: FormInstance
) => {
  const errors = rejection?.error?.error?.response?.data?.errors
  const fieldErrors = langList.flatMap((lang) => {
    const titleErrors = errors?.[`${lang.code}.title`]
    return titleErrors?.length
      ? [{ name: [lang.code, 'title'], errors: titleErrors }]
      : []
  })

  if (fieldErrors.length) {
    form.setFields(fieldErrors)
  }

  throw rejection
}

export const EventForm: SBRMForm = forwardRef(({ formProps }, ref) => {
  const dispatch = useAppDispatch()
  const { error } = useAppSelector((state) => state.event)

  const composeFormData = (values: any): FormData => {
    const data = new FormData()
    const fields = [
      'id',
      'address',
      'venue',
      'end',
      'start',
      'isPublished',
      'maxGuestsPerParticipant',
      'maxParticipants',
      'maxMembers',
      'maxGuests',
      'maxParticipants',
      'participationCutoff',
      'cancelParticipationCutoff',
      'bookingMethod',
    ]
    fields.forEach((field) => {
      const value = values[field]
      if (value === undefined || value === null) {
        return
      }
      data.append(field, value)
    })

    const localizedFields = langList.map((lang) => lang.code)
    localizedFields.map((field) => {
      const value = values[field]
      data.append(field + '[title]', value.title)
      data.append(field + '[description]', value.description)
      if (
        value.reminderDescription !== undefined &&
        value.reminderDescription !== null
      ) {
        data.append(field + '[reminderDescription]', value.reminderDescription)
      }
      if (
        value.eventChatRequestMessage !== undefined &&
        value.eventChatRequestMessage !== null
      ) {
        data.append(
          field + '[eventChatRequestMessage]',
          value.eventChatRequestMessage
        )
      }
    })

    if (values.membershipPlans) {
      values.membershipPlans.forEach((plan: any) => {
        data.append('membershipPlans[]', plan)
      })
    }

    if (values.unsplash) {
      data.append('unsplash[url]', values.unsplash.url)
      data.append('unsplash[blurHash]', values.unsplash.blurHash)
    } else if (values.image) {
      data.append('image', values.image[0].originFileObj)
    }
    return data
  }

  return (
    <SBRM.BaseEntityForm
      {...{ ref, formProps }}
      error={error && String(error)}
      formActions={{
        create: Create,
        update: Update,
        delete: Delete,
      }}
      handlers={{
        create: async (values, form) =>
          dispatch(createEvent(composeFormData(values)))
            .then(() => true)
            .catch((rejection) => mapTitleErrorsAndRethrow(rejection, form)),
        update: async (values, form) =>
          dispatch(updateEvent(composeFormData(values)))
            .then(() => true)
            .catch((rejection) => mapTitleErrorsAndRethrow(rejection, form)),
        delete: async (values) =>
          dispatch(deleteEvent(values)).then(() => {
            return true
          }),
      }}
    />
  )
})

EventForm.displayName = 'EventForm'

export default EventForm
